import React, { useState, useEffect, useRef } from 'react';

const DraggableNumberPicker = ({ initialValue = 4, onNumberChange }) => {
  const [selectedNumber, setSelectedNumber] = useState(initialValue);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollContainerRef = useRef(null);
  
  // Create an array of numbers 1-19
  const numbers = Array.from({ length: 19 }, (_, i) => i + 1);

  // Function to scroll to a specific number
  const scrollToItem = (number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const items = container.querySelectorAll('.number-picker-item');
      const index = numbers.indexOf(number);

      if (index !== -1 && items[index]) {
        const item = items[index];
        const containerWidth = container.offsetWidth;
        const itemWidth = item.offsetWidth;
        const scrollLeft = item.offsetLeft - (containerWidth / 2) + (itemWidth / 2);

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  // Debounce function to limit how often scroll events fire
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Handle scroll events to update selected number
  const handleScroll = debounce(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const items = [...container.querySelectorAll('.number-picker-item')];
      const containerMiddle = container.scrollLeft + (container.offsetWidth / 2);

      // Find the closest item to the center
      let closestItem = null;
      let closestDistance = Infinity;

      items.forEach((item) => {
        const itemMiddle = item.offsetLeft + (item.offsetWidth / 2);
        const distance = Math.abs(containerMiddle - itemMiddle);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      });

      if (closestItem) {
        const number = parseInt(closestItem.textContent.trim(), 10);
        if (number !== selectedNumber) {
          setSelectedNumber(number);
          if (onNumberChange) onNumberChange(number);
        }
      }
    }
  }, 100);

  // Mouse/Touch event handlers for dragging
  const handleMouseDown = (e) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    }
  };

  const handleTouchStart = (e) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    if (scrollContainerRef.current) {
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Multiply by 2 for faster scrolling
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    if (scrollContainerRef.current) {
      const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - startX) * 2;
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    handleScroll();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    handleScroll();
  };

  // Add event listeners on mount and clean up on unmount
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // For scroll handling
      container.addEventListener('scroll', handleScroll);
      
      // For drag handling
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleTouchEnd);
      
      // Initial scroll to selected number
      scrollToItem(selectedNumber);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isDragging, startX, scrollLeft]);

  // If selected number changes externally, scroll to it
  useEffect(() => {
    scrollToItem(selectedNumber);
  }, [selectedNumber]);

  return (
    <div
      className="number-picker-container"
      style={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        overflowX: 'auto',
        scrollbarWidth: 'none', // Hide scrollbar for Firefox
        msOverflowStyle: 'none', // Hide scrollbar for IE
        WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }}
      ref={scrollContainerRef}
    >
      <div
        className="number-picker-track"
        style={{
          display: 'flex',
          padding: '0 40%', // Add padding to allow center positioning
          alignItems: 'center',
          minWidth: 'min-content',
        }}
      >
        {numbers.map((num) => (
          <div
            key={num}
            className={`number-picker-item ${selectedNumber === num ? 'active' : ''}`}
            onClick={() => {
              setSelectedNumber(num);
              if (onNumberChange) onNumberChange(num);
              // Scroll to center this item
              scrollToItem(num);
            }}
            style={{
              minWidth: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: selectedNumber === num ? '#ff9800' : '#808080',
              fontWeight: selectedNumber === num ? 'bold' : 'normal',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              transform: selectedNumber === num ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            {num}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DraggableNumberPicker;